import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { handlerDrawerOpen, useGetMenuMaster } from '~/api/menu';
import Loader from '~/components/Loader';
import { useAccounts } from '~/hooks/useAccounts';
import useAuth from '~/hooks/useAuth';
import useConfig from '~/hooks/useConfig';
import { useExternalTopBanner } from '~/hooks/useExternalTopBanner';
import AuthGuard from '~/utils/route-guard/AuthGuard';

import Drawer from './Drawer';
import Footer from './Footer';
import Header from './Header';

const useHubspotIdentify = () => {
  const { user } = useAuth();
  const [isTracked, setIsTracked] = useState(false);

  useEffect(() => {
    if (user?.email && !isTracked) {
      // @ts-ignore
      const hubspotTracker = (window._hsq = window._hsq || []);

      // Identify the user by email (most common)
      hubspotTracker.push([
        'identify',
        {
          email: user.email,
        },
      ]);

      // Trigger the identity to be sent to HubSpot
      hubspotTracker.push(['trackPageView']);

      setIsTracked(true);
    }
  }, [user, isTracked]);
};

const calculateMarginTop = (
  user: any,
  hasPaymentMethods: boolean,
  externalBannerHeight: number,
) => {
  let margin = externalBannerHeight;
  // Each banner is approximately 40px tall
  if (user?.needsVerification) margin += 40;
  if (user?.isLocked) margin += 40;
  if (!hasPaymentMethods) margin += 40;
  return margin || 'inherit';
};

const DashboardLayout = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const { menuMasterLoading } = useGetMenuMaster();
  const matchDownXL = useMediaQuery(theme.breakpoints.down('xl'));
  const [hasPaymentMethods, setHasPaymentMethods] = useState(true);
  const { miniDrawer } = useConfig();
  const externalBannerHeight = useExternalTopBanner();
  const { data: accountsData, isLoading: isAccountsLoading } = useAccounts();
  const accounts = accountsData?.pages?.flatMap((page) => page.accounts) ?? [];
  const hasAccounts = accounts.length > 0;
  const shouldShowNewTraderExperience = !hasAccounts;

  // for banner tracking or whatever
  useHubspotIdentify();

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      handlerDrawerOpen(!matchDownXL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchDownXL]);

  if (menuMasterLoading || (isAccountsLoading && !accountsData)) return <Loader />;

  return (
    <AuthGuard>
      <div className="flex">
        <Header
          userLocked={user?.isLocked === true}
          userLockedType={user?.lockType}
          onPaymentMethodsChange={setHasPaymentMethods}
          externalBannerOffset={externalBannerHeight}
        />
        <Drawer externalBannerOffset={externalBannerHeight} />

        <main className="min-w-0 flex-1 p-4 sm:p-6">
          <div
            className="h-14 sm:h-16"
            style={{
              marginTop: calculateMarginTop(user, hasPaymentMethods, externalBannerHeight),
            }} // Must use style prop until migrate the navigation
          />
          <div className="flex min-h-[calc(100vh-110px)] flex-col">
            <Outlet />
            {shouldShowNewTraderExperience ? null : <Footer />}
          </div>
        </main>
      </div>
    </AuthGuard>
  );
};

export default DashboardLayout;
